/**
 * Тонкая обёртка над window.Telegram.WebApp SDK.
 *
 * Зачем не использовать @telegram-apps/sdk-react: он молодой, ломает API
 * между мажорами, и нам пока нужны всего 4 штуки — initData, theme, expand,
 * BackButton. Раздувать стек под это смысла нет.
 */

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

export interface TelegramWebAppUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramWebAppUser;
    auth_date?: number;
    hash?: string;
    query_id?: string;
    start_param?: string;
  };
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: "light" | "medium" | "heavy") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  showAlert: (message: string) => void;
  showPopup?: (params: {
    title?: string;
    message: string;
    buttons?: { id?: string; type?: "default" | "ok" | "close" | "cancel" | "destructive"; text?: string }[];
  }, callback?: (button_id: string) => void) => void;
  openLink?: (url: string) => void;
  openTelegramLink?: (url: string) => void;
  /** Bot API 8.0: зберегти файл за адресою на пристрій людини. */
  downloadFile?: (params: { url: string; file_name: string }, callback?: (accepted: boolean) => void) => void;
  isVersionAtLeast?: (version: string) => boolean;
}

/** Безопасный доступ к WebApp — может быть null, если открыли в обычном браузере (не из бота). */
export function tg(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/** Сырой initData для API-запросов (отправляется в `X-Telegram-Init-Data`). */
export function getInitData(): string {
  return tg()?.initData ?? "";
}

/** Удобный shortcut на текущего юзера из initData (не валидированный — для UI). */
export function getCurrentTelegramUser(): TelegramWebAppUser | null {
  return tg()?.initDataUnsafe?.user ?? null;
}

/** Викликати в `main.tsx` — повідомляє Telegram що ми готові + розгортає
 * на повний екран. Bot API 8.0+ підтримує справжній FULLSCREEN режим
 * (як у BotFather) — пробуємо його, fallback на expand() якщо клієнт
 * старий. Verbose try/catch — старі Telegram desktop/web версії не
 * мають requestFullscreen → expand() лишається.
 */
export function initTelegramApp(): void {
  const webapp = tg() as any;
  if (!webapp) return;
  // ready()/expand() у try/catch — у деяких Telegram-клієнтів кидали помилку,
  // що зривала весь модуль до монтування React (owner 2026-07-08, @kutnaksu).
  try {
    webapp.ready();
  } catch {
    /* ignore */
  }
  try {
    webapp.expand();
  } catch {
    /* ignore */
  }

  // Ховаємо нативну Telegram-кнопку «Back» — у застосунку своя навігація
  // (Меню/Назад), нативна зверху зайва (юзер 2026-06-12).
  try {
    webapp.BackButton?.hide?.();
  } catch {
    /* ignore */
  }

  // Fullscreen — ТІЛЬКИ на мобільних (юзер 2026-06-11: «на ПК відкривається
  // на весь екран, незручно, нема десктоп-дизайну — зроби на компі віконний
  // режим»). На desktop/web лишаємо звичайне вікно міні-аппи (expand). Це
  // також чинить десктопне «Закрити» у 2 тапи (раніше перший тап виходив з
  // fullscreen, другий закривав). Мобільні платформи: ios / android.
  const platform: string =
    typeof webapp.platform === "string" ? webapp.platform : "";
  const isMobile = platform === "ios" || platform === "android";

  if (isMobile) {
    // Bot API 8.0+ → справжній FULLSCREEN як у BotFather.
    try {
      if (typeof webapp.requestFullscreen === "function") {
        webapp.requestFullscreen();
      }
    } catch {
      /* старі клієнти — ігноруємо, expand() вже зробив своє */
    }
    // disableVerticalSwipes — щоб свайп вниз не закривав app у fullscreen.
    try {
      if (typeof webapp.disableVerticalSwipes === "function") {
        webapp.disableVerticalSwipes();
      }
    } catch {
      /* ignore */
    }
  }

  // Колір фону webview = наш cream bg-bg (#F5F1E8). Без цього при
  // overscroll/rubber-band проступає БІЛИЙ дефолтний фон Telegram-webview
  // (юзер 2026-06-11: «видно білі границі за апкою»). Тепер overscroll-зона
  // зливається з фоном застосунку.
  try {
    if (typeof webapp.setBackgroundColor === "function") {
      webapp.setBackgroundColor("#F5F1E8");
    }
    if (typeof webapp.setBottomBarColor === "function") {
      webapp.setBottomBarColor("#F5F1E8");
    }
    // Верхня зона (header) теж cream — при overscroll ВГОРУ раніше
    // проступав інший колір (юзер 2026-06-11: «вниз фон однаковий, а вгору
    // відрізняється»). Тепер усі керовані Telegram зони однакові.
    if (typeof webapp.setHeaderColor === "function") {
      webapp.setHeaderColor("#F5F1E8");
    }
  } catch {
    /* старі клієнти — ігноруємо */
  }
}
